import React from 'react';
import { motion as Motion, useScroll, useTransform } from 'framer-motion';

/* Shared scroll-linked rotation for small graphic marks (registration
   marks, crosshairs) — one full turn per ~2.5 page-heights of scroll, tied
   to the whole document's scroll progress so every instance on the page
   turns in sync, reading as one shared "instrument dial" rather than
   independent spinners. Framer's useScroll already drives the site's
   existing hero parallax through Lenis, so this is the same proven path. */
const useScrollRotate = (turns = 2.5) => {
  const { scrollYProgress } = useScroll();
  return useTransform(scrollYProgress, [0, 1], [0, 360 * turns]);
};

/* Shared pace for the auto-scrolling hero USP marquees (RapidPrototyping,
   AIVoiceAgent, the main site's own hero, SolutionPageTemplate's brand row).
   That marquee's CSS animation (`.animate-infinite-scroll-fast`, defined in
   index.css) moves a fixed 50% of its track in a flat 32s regardless of how
   many items are in it — so a page with 10 items covers 67% more distance in
   the same 32s as the 6-item baseline and visibly races past it, which is
   what made the Prototyping marquee read as faster than the rest of the site.
   marqueeDuration(items) instead sizes the loop to the content, at this one
   fixed per-item rate, so every page's marquee moves at the same felt speed
   no matter how many USPs it lists. 32/6 is that rate, anchored to the main
   hero's marquee (6 items) since it was the original, unscaled baseline every
   other page's pace was implicitly supposed to match. */
const MARQUEE_SECONDS_PER_ITEM = 32 / 6;
export const marqueeDuration = (itemCount) => `${(itemCount * MARQUEE_SECONDS_PER_ITEM).toFixed(1)}s`;

/* The scrolling USP track every hero marquee uses (main site, case studies,
   Concierge, Rapid Prototyping). Labels separated by a dot, no counters;
   type comes from .marquee-label in index.css. Each page keeps its own outer
   bar (position, border, entrance) and renders this inside it. Two copies of
   the list make the loop seamless — the second is aria-hidden. */
export const HeroMarqueeTrack = ({ items, ink = '#ffffff', accent, bg }) => (
  <div
    className="flex will-change-transform animate-infinite-scroll-fast py-4 md:py-6"
    style={{ animationDuration: marqueeDuration(items.length), backgroundColor: bg }}
  >
    {[0, 1].map((set) => (
      <div key={set} className="flex items-center flex-shrink-0" aria-hidden={set === 1 ? true : undefined}>
        {items.map((label, i) => (
          <React.Fragment key={i}>
            <span className="marquee-label px-5 md:px-8" style={{ color: ink }}>{label}</span>
            <span className="marquee-label" aria-hidden="true" style={{ color: accent || ink }}>·</span>
          </React.Fragment>
        ))}
      </div>
    ))}
  </div>
);

/* --------------------------------------------------------------------------------
   SWISS MICROGRAPHIC SYSTEM
   A small, reusable set of print-inspired micrographic components used across
   CRE8NOW to add technical/editorial detail: registration marks, crop marks,
   index tags, coordinates, vertical labels, measurement bars and flow chains.
   Pure CSS/SVG, theme-aware via currentColor + CSS vars, no runtime cost.

   Every piece animates in once as it scrolls into view (IntersectionObserver,
   see `useReveal` below) and a few carry a subtle idle loop — a slow spin, a
   breathing dot, a flowing arrow — so the frame reads as "live" instrumentation
   rather than static decoration. All idle loops are defined in index.css
   (.animate-swiss-*) to keep this file dependency-free.
-------------------------------------------------------------------------------- */

/* Toggles `visible` every time the element crosses the viewport threshold —
   true scrolling in, false scrolling back out — so the reveal isn't a
   one-shot "appear once on first load" but a repeatable in/out effect as
   the page scrolls past it in either direction. One IntersectionObserver
   per instance, kept alive for the component's lifetime (not disconnected
   after the first hit); still cheap since it's event-driven, not per-frame
   scroll math, so it's safe across dozens of instances per page. */
const useReveal = (threshold = 0.15) => {
  const ref = React.useRef(null);
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const obs = new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting);
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

export const RegistrationMark = ({ size = 18, className = '', style = {}, animate = true }) => {
  const [ref, visible] = useReveal();
  const scrollRotate = useScrollRotate();
  return (
    <Motion.svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={[
        animate && 'transition-[opacity,transform] duration-700 ease-out',
        animate && (visible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'),
        className,
      ].filter(Boolean).join(' ')}
      style={{ ...style, rotate: animate ? scrollRotate : 0 }}
    >
      <g className={animate ? 'animate-swiss-spin-slow' : ''}>
        <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1" />
        <line x1="12" y1="0" x2="12" y2="24" stroke="currentColor" strokeWidth="1" />
        <line x1="0" y1="12" x2="24" y2="12" stroke="currentColor" strokeWidth="1" />
      </g>
    </Motion.svg>
  );
};

/* Four corner crop marks. Parent must be position:relative (or absolute).
   `inset` sets all four by default; pass insetTop/insetBottom/insetX to
   pull marks off a shared edge that's covered by chrome (a fixed nav bar,
   a bottom marquee) so they stay visible instead of painting underneath it.
   Pass showTop={false} / showBottom={false} to drop a pair entirely.
   Each corner slides in from its own direction, staggered, the first time
   the frame scrolls into view. */
export const CropMarks = ({ inset = 14, insetTop, insetBottom, insetX, showTop = true, showBottom = true, className = '', color }) => {
  const [ref, visible] = useReveal();
  const s = { borderColor: color || 'currentColor' };
  const top = insetTop ?? inset;
  const bottom = insetBottom ?? inset;
  const x = insetX ?? inset;
  const base = 'absolute w-[10px] h-[10px] transition-[opacity,transform] duration-700 ease-out';
  const corner = (edge, delay) => ({
    style: { ...s, [edge.v]: edge.v === 'top' ? top : bottom, [edge.h]: x, transitionDelay: `${delay}ms` },
    className: [
      base,
      edge.v === 'top' ? 'border-t' : 'border-b',
      edge.h === 'left' ? 'border-l' : 'border-r',
      visible ? 'opacity-100 translate-x-0 translate-y-0' : `opacity-0 ${edge.h === 'left' ? '-translate-x-1' : 'translate-x-1'} ${edge.v === 'top' ? '-translate-y-1' : 'translate-y-1'}`,
    ].join(' '),
  });
  return (
    <div ref={ref} className={`pointer-events-none absolute inset-0 z-20 ${className}`} aria-hidden="true">
      {showTop && <span {...corner({ v: 'top', h: 'left' }, 0)} />}
      {showTop && <span {...corner({ v: 'top', h: 'right' }, 80)} />}
      {showBottom && <span {...corner({ v: 'bottom', h: 'left' }, 160)} />}
      {showBottom && <span {...corner({ v: 'bottom', h: 'right' }, 240)} />}
    </div>
  );
};

/* "D / 001" style micro index tag */
export const IndexTag = ({ code, num, color, className = '', dot = true }) => {
  const [ref, visible] = useReveal();
  return (
    <span
      ref={ref}
      className={`inline-flex items-center gap-2 font-mono-swiss text-[9px] md:text-[10px] font-medium uppercase tracking-[0.25em] transition-[opacity,transform] duration-500 ease-out ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-1'} ${className}`}
    >
      {dot && <span className="inline-block w-[5px] h-[5px] animate-swiss-pulse" style={{ backgroundColor: color || 'currentColor' }} />}
      <span>{code} / {num}</span>
    </span>
  );
};

/* "X:0284 — Y:0512" style coordinate readout */
export const CoordTag = ({ x, y, className = '' }) => {
  const [ref, visible] = useReveal();
  return (
    <span
      ref={ref}
      className={`font-mono-swiss text-[9px] md:text-[10px] tracking-[0.2em] whitespace-nowrap tabular-nums transition-[opacity,transform] duration-500 ease-out ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-1'} ${className}`}
    >
      X:{String(x).padStart(4, '0')} / Y:{String(y).padStart(4, '0')}
    </span>
  );
};

/* Same readout, but live — tracks the pointer across the viewport.
   Position is sampled into a ref on every mousemove (cheap) and only
   flushed to React state via rAF when the rounded value actually changes,
   so it stays "live" without forcing 60fps re-renders on every pixel. */
export const LiveCoordTag = ({ className = '' }) => {
  const posRef = React.useRef({ x: 0, y: 0 });
  const [display, setDisplay] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    const onMove = (e) => { posRef.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', onMove, { passive: true });

    let raf;
    const tick = () => {
      setDisplay((prev) => {
        const x = Math.round(posRef.current.x);
        const y = Math.round(posRef.current.y);
        return (prev.x === x && prev.y === y) ? prev : { x, y };
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return <CoordTag x={display.x} y={display.y} className={className} />;
};

/* "03 / 09" style section counter */
export const SectionCounter = ({ current, total, className = '' }) => {
  const [ref, visible] = useReveal();
  return (
    <span
      ref={ref}
      className={`font-mono-swiss text-[9px] md:text-[10px] tracking-[0.2em] transition-[opacity,transform] duration-500 ease-out ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'} ${className}`}
    >
      {String(current).padStart(2, '0')} / {String(total).padStart(2, '0')}
    </span>
  );
};

/* Rotated label pinned to an edge. Parent must be position:relative.
   `offset` is the distance (px) from that edge — keep it in sync with
   whatever inset the rest of the frame's micro elements use.

   Uses CSS writing-mode instead of a manual `rotate()` transform: with a
   plain rotate, the element's own (pre-rotation) layout box is a single
   thin line, so a percentage `translateY(-50%)` — meant to center it —
   resolves against that tiny line-height instead of the text's actual
   rendered (rotated) length, and the label ends up hanging entirely to
   one side of center instead of straddling it. `vertical-rl` makes the
   browser lay the text out vertically to begin with, so its layout height
   *is* the rendered length and `top: 50%` + `translateY(-50%)` centers it
   correctly. `rotate(180deg)` on the left side flips the reading direction
   (vertical-rl alone always reads top-to-bottom). */
export const VerticalLabel = ({ text, side = 'left', offset = 0, className = '', color }) => {
  const [ref, visible] = useReveal();
  const travel = side === 'left' ? -8 : 8;

  // Local scroll-linked parallax — drifts along the label's own vertical
  // axis as it passes through the viewport, independent of the reveal-in
  // animation above. The left side sits inside a rotate(180deg) parent (so
  // vertical-rl text reads top-to-bottom), which flips the local Y axis —
  // the range is mirrored for "left" so both sides drift the same physical
  // screen direction instead of opposite ways.
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const amp = 90;
  const parallaxY = useTransform(scrollYProgress, [0, 1], side === 'left' ? [amp, -amp] : [-amp, amp]);

  return (
    <span
      ref={ref}
      className={`pointer-events-none absolute top-1/2 font-mono-swiss text-[9px] md:text-[10px] font-medium uppercase tracking-[0.35em] whitespace-nowrap transition-[opacity,transform] duration-700 ease-out ${visible ? 'opacity-100' : 'opacity-0'} ${className}`}
      style={{
        left: side === 'left' ? offset : undefined,
        right: side === 'right' ? offset : undefined,
        writingMode: 'vertical-rl',
        transform: `translateY(-50%) translateX(${visible ? 0 : travel}px) ${side === 'left' ? 'rotate(180deg)' : ''}`,
        color: color || 'currentColor',
      }}
      aria-hidden="true"
    >
      <Motion.span style={{ display: 'inline-block', y: parallaxY }}>{text}</Motion.span>
    </span>
  );
};

/* Thin measurement bar with tick marks — grows in tick by tick */
export const MicroBar = ({ ticks = 16, className = '', color }) => {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} className={`flex items-end h-3 w-full gap-[3px] ${className}`} aria-hidden="true">
      {Array.from({ length: ticks }).map((_, i) => (
        <span
          key={i}
          style={{
            backgroundColor: color || 'currentColor',
            height: i % 4 === 0 ? '100%' : '40%',
            transitionDelay: `${i * 20}ms`,
          }}
          className={`flex-1 opacity-30 origin-bottom transition-transform duration-500 ease-out ${visible ? 'scale-y-100' : 'scale-y-0'}`}
        />
      ))}
    </div>
  );
};

/* "INPUT → PROCESS → OUTPUT" style flow chain — arrows pulse in sequence
   to read as data moving left to right. */
export const FlowChain = ({ steps = [], color, className = '' }) => (
  <div className={`flex items-center flex-wrap gap-x-3 gap-y-1 font-mono-swiss text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.25em] ${className}`}>
    {steps.map((step, i) => (
      <React.Fragment key={step}>
        <span style={{ color: i === steps.length - 1 ? (color || 'currentColor') : undefined }}>
          {step}
        </span>
        {i < steps.length - 1 && (
          <span className="animate-swiss-flow inline-block" style={{ animationDelay: `${i * 0.3}s` }}>&rarr;</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

/* Soft halftone dot field, purely decorative — drifts very slowly */
export const HalftoneDots = ({ className = '', opacity = 0.15, color = 'currentColor', size = 5 }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute animate-swiss-pan ${className}`}
    style={{
      opacity,
      backgroundImage: `radial-gradient(${color} 1px, transparent 1px)`,
      backgroundSize: `${size}px ${size}px`,
      color: color === 'currentColor' ? undefined : color,
    }}
  />
);

/* Repeating thin scanner lines, purely decorative, with a slow scan sweep */
export const ScannerLines = ({ className = '', opacity = 0.08 }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    style={{
      opacity,
      backgroundImage: 'repeating-linear-gradient(0deg, currentColor 0px, currentColor 1px, transparent 1px, transparent 4px)',
    }}
  >
    <div className="animate-swiss-scan absolute inset-x-0 h-1/3" style={{ background: 'linear-gradient(180deg, transparent, currentColor, transparent)' }} />
  </div>
);

/* Small barcode-like structure, purely decorative — idles like an equalizer */
export const BarcodeMark = ({ bars = 24, className = '', color }) => {
  const widths = React.useMemo(
    () => Array.from({ length: bars }, (_, i) => (((i * 37) % 5) + 1)),
    [bars]
  );
  return (
    <div className={`flex items-stretch h-4 gap-[2px] ${className}`} aria-hidden="true">
      {widths.map((w, i) => (
        <span
          key={i}
          style={{ width: w, backgroundColor: color || 'currentColor', animationDelay: `${(i % 7) * 0.12}s` }}
          className="opacity-40 animate-swiss-eq"
        />
      ))}
    </div>
  );
};

/* Faint 1px column grid, purely decorative — the underlying Swiss grid
   structure made just barely visible. Parent must be position:relative. */
export const GridOverlay = ({ columns = 12, opacity = 0.05, className = '', color }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute inset-0 grid ${className}`}
    style={{ gridTemplateColumns: `repeat(${columns}, 1fr)`, opacity, color: color || 'currentColor' }}
  >
    {Array.from({ length: columns }).map((_, i) => (
      <div key={i} className="h-full border-l last:border-r" style={{ borderColor: 'currentColor' }} />
    ))}
  </div>
);

/* Broken-cross crosshair — a print crosshair with a gap at the center,
   distinct from RegistrationMark's circled version. */
export const Crosshair = ({ size = 14, className = '', color }) => {
  const [ref, visible] = useReveal();
  const scrollRotate = useScrollRotate();
  return (
    <Motion.svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      className={`transition-[opacity,transform] duration-500 ease-out ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'} ${className}`}
      style={{ color: color || 'currentColor', rotate: scrollRotate }}
    >
      <line x1="7" y1="0" x2="7" y2="5" stroke="currentColor" strokeWidth="1" />
      <line x1="7" y1="9" x2="7" y2="14" stroke="currentColor" strokeWidth="1" />
      <line x1="0" y1="7" x2="5" y2="7" stroke="currentColor" strokeWidth="1" />
      <line x1="9" y1="7" x2="14" y2="7" stroke="currentColor" strokeWidth="1" />
    </Motion.svg>
  );
};

/* Tiny key/value technical metadata stack, e.g. [['REV', 'A.03'], ['LANG', 'DE']] */
export const MetaBlock = ({ rows = [], className = '', color }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`flex flex-col gap-1 font-mono-swiss text-[8px] md:text-[9px] tracking-[0.15em] uppercase transition-[opacity,transform] duration-500 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'} ${className}`}
      aria-hidden="true"
    >
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <span className="opacity-50">{k}</span>
          <span className="w-3 h-px opacity-30" style={{ backgroundColor: color || 'currentColor' }} />
          <span style={{ color: color || 'currentColor' }}>{v}</span>
        </div>
      ))}
    </div>
  );
};

/* Labeled technical rule — a hairline with end-ticks and an optional
   centered label, like a print spec sheet's measurement callout. */
export const TechnicalRule = ({ label, className = '', color }) => (
  <div className={`flex items-center gap-3 ${className}`} aria-hidden="true">
    <span className="w-px h-2 flex-shrink-0" style={{ backgroundColor: color || 'currentColor' }} />
    <span className="flex-1 h-px opacity-25" style={{ backgroundColor: color || 'currentColor' }} />
    {label && (
      <span className="font-mono-swiss text-[8px] md:text-[9px] tracking-[0.25em] uppercase whitespace-nowrap" style={{ color: color || 'currentColor' }}>
        {label}
      </span>
    )}
    <span className="flex-1 h-px opacity-25" style={{ backgroundColor: color || 'currentColor' }} />
    <span className="w-px h-2 flex-shrink-0" style={{ backgroundColor: color || 'currentColor' }} />
  </div>
);

/* Small directional arrow glyph — a technical pointer, not a UI affordance. */
export const TinyArrow = ({ direction = 'right', size = 10, className = '', color }) => {
  const rotations = { right: 0, down: 90, left: 180, up: -90 };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 10 10"
      fill="none"
      aria-hidden="true"
      className={className}
      style={{ color: color || 'currentColor', transform: `rotate(${rotations[direction] ?? 0}deg)` }}
    >
      <line x1="0" y1="5" x2="8" y2="5" stroke="currentColor" strokeWidth="1" />
      <path d="M5 2L8 5L5 8" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  );
};

/* "PG. 03 / 12" style page index — pagination framing for a full section/page,
   distinct from SectionCounter's bare fraction. */
export const PageIndex = ({ current, total, className = '', color }) => (
  <span className={`font-mono-swiss text-[9px] md:text-[10px] tracking-[0.25em] uppercase ${className}`} style={{ color: color || 'currentColor' }}>
    PG. {String(current).padStart(2, '0')} / {String(total).padStart(2, '0')}
  </span>
);

/* Small outlined (or filled) rectangle — a plain technical accent block. */
export const ThinRect = ({ width = 24, height = 8, filled = false, className = '', color }) => (
  <span
    aria-hidden="true"
    className={`inline-block flex-shrink-0 ${className}`}
    style={{
      width,
      height,
      border: `1px solid ${color || 'currentColor'}`,
      backgroundColor: filled ? (color || 'currentColor') : 'transparent',
    }}
  />
);

/* Small pulsing status marker with a label, e.g. "SYSTEM / ONLINE". */
export const SystemMarker = ({ label = 'SYSTEM / ONLINE', className = '', color }) => (
  <div className={`inline-flex items-center gap-2 font-mono-swiss text-[8px] md:text-[9px] tracking-[0.2em] uppercase ${className}`} aria-hidden="true">
    <span className="w-[5px] h-[5px] rounded-full animate-swiss-pulse flex-shrink-0" style={{ backgroundColor: color || 'currentColor' }} />
    <span className="opacity-60">{label}</span>
  </div>
);

/* Subtle print-grain noise texture, purely decorative and extremely faint —
   an SVG fractal-noise filter rendered as a tiled data URI so there's no
   extra asset request. */
const GRAIN_SVG_URL = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/></filter><rect width="100%" height="100%" filter="url(#n)"/></svg>'
)}`;
export const GrainOverlay = ({ className = '', opacity = 0.05, fixed = false }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none ${fixed ? 'fixed' : 'absolute'} inset-0 mix-blend-overlay ${className}`}
    style={{ opacity, backgroundImage: `url("${GRAIN_SVG_URL}")`, backgroundSize: '60px 60px' }}
  />
);

/* Print misregistration — offsets a magenta/cyan ghost of the content by a
   hairline behind the real text, mimicking a slightly-off color separation.
   Keep `offset` small (1-2px); it's meant to be felt more than seen. */
export const Misregister = ({ children, offset = 1, className = '' }) => (
  <span className={`relative inline-block ${className}`}>
    <span aria-hidden="true" className="absolute inset-0 opacity-60 mix-blend-multiply select-none" style={{ color: '#F41465', transform: `translate(${offset}px, 0)` }}>
      {children}
    </span>
    <span aria-hidden="true" className="absolute inset-0 opacity-60 mix-blend-multiply select-none" style={{ color: '#2563EB', transform: `translate(-${offset}px, 0)` }}>
      {children}
    </span>
    <span className="relative">{children}</span>
  </span>
);

/* Wraps content with corner crop marks + top index tag + bottom-right counter.
   Drop onto any `position:relative` section for a consistent technical frame. */
export const SwissFrame = ({ children, code, num, current, total, color, className = '', showMarks = true }) => (
  <div className={`relative ${className}`}>
    {showMarks && <CropMarks color={color} />}
    {(code || num) && (
      <div className="absolute top-4 left-4 md:top-6 md:left-6 z-20">
        <IndexTag code={code} num={num} color={color} />
      </div>
    )}
    {(current || total) && (
      <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 z-20">
        <SectionCounter current={current} total={total} />
      </div>
    )}
    {children}
  </div>
);

/* The hero's corner-frame system (top-corner crop marks, index tag +
   registration mark, live coordinate readout, vertical edge labels),
   packaged as a single absolute overlay so any section can drop it in one
   line. `topOffset` defaults to clearing the fixed nav (96px) — every
   section can be scrolled flush to the viewport top, same as the hero. */
export const SectionFrame = ({ code, num, color, verticalLabel = 'CRE8NOW / SYSTEM', verticalLabelRight, topOffset = 96, className = '', showVerticalLabels = true }) => (
  <div className={`hidden md:block absolute inset-0 z-10 pointer-events-none ${className}`} aria-hidden="true">
    <CropMarks insetTop={8} insetX={8} showBottom={false} color={color} />
    <div className="absolute left-6 flex items-center gap-4" style={{ top: topOffset }}>
      <RegistrationMark size={16} style={{ color }} className="opacity-70" />
      <IndexTag code={code} num={num} color={color} />
    </div>
    <div className="absolute right-6 flex items-center gap-4" style={{ top: topOffset }}>
      <LiveCoordTag />
      <RegistrationMark size={16} style={{ color }} className="opacity-70" />
    </div>
    {showVerticalLabels && <VerticalLabel text={verticalLabel} side="left" offset={24} />}
    {showVerticalLabels && verticalLabelRight && <VerticalLabel text={verticalLabelRight} side="right" offset={24} />}
  </div>
);
